import React, { useCallback, useEffect, useState } from 'react';
import { Typography, Button, Spin, Alert, Empty } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';

import { professorTurmasService } from '../../services/api';
import { buildApiErrorMessage } from '../../utils/apiError';
import TurmaProfessorCard from './TurmaProfessorCard';
import '../../styles/PortalProfessor.css';

const { Title, Text } = Typography;

const MENSAGEM_SEM_VINCULO =
  'Seu usuário ainda não está vinculado a um professor/equipe. Procure a administração.';

// Portal do Professor -- Turmas (Fase 14C). Somente leitura: sem Nova
// Turma/Editar/Excluir/Recalcular Aulas/Matricular aluno/Exportar PDF.
// Nenhuma dessas ações existe nesta tela -- a ausência é a proteção,
// mesmo padrão já usado em Aulas Dadas do Portal. Backend continua sendo
// a autoridade sobre quais turmas aparecem (escopo vem exclusivamente da
// sessão autenticada); o frontend não faz nenhum filtro de segurança.
const PortalProfessorTurmas = () => {
  const [turmas, setTurmas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null); // { status, message } | null

  const carregarTurmas = useCallback(async () => {
    setLoading(true);
    setErro(null);
    try {
      const response = await professorTurmasService.listar();
      setTurmas(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 403) {
        setErro({ status, message: MENSAGEM_SEM_VINCULO });
      } else {
        const { message: mensagem } = buildApiErrorMessage(
          err,
          'Não foi possível carregar suas turmas. Tente novamente.',
          { context: status === 401 ? 'session' : undefined }
        );
        setErro({ status, message: mensagem });
      }
      setTurmas([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    carregarTurmas();
  }, [carregarTurmas]);

  const renderConteudo = () => {
    if (loading) {
      return (
        <div className="portal-professor__state" role="status" aria-live="polite">
          <Spin size="large" />
          <Text type="secondary">Carregando suas turmas...</Text>
        </div>
      );
    }

    if (erro) {
      return (
        <div className="portal-professor__state">
          <Alert
            type={erro.status === 403 ? 'warning' : 'error'}
            showIcon
            message={erro.status === 403 ? 'Acesso indisponível' : 'Não foi possível carregar'}
            description={erro.message}
            style={{ maxWidth: 480, textAlign: 'left' }}
          />
          {erro.status !== 403 && (
            <Button icon={<ReloadOutlined />} onClick={carregarTurmas}>
              Tentar novamente
            </Button>
          )}
        </div>
      );
    }

    if (turmas.length === 0) {
      return (
        <div className="portal-professor__state">
          <Empty description="Nenhuma turma encontrada." />
        </div>
      );
    }

    return (
      <div className="portal-professor__list">
        {turmas.map((turma) => (
          <TurmaProfessorCard key={turma.id} turma={turma} />
        ))}
      </div>
    );
  };

  return (
    <div className="portal-professor">
      <div className="portal-professor__header">
        <div>
          <Title level={3} className="portal-professor__title">
            Turmas
          </Title>
          <Text type="secondary" className="portal-professor__subtitle">
            Suas turmas
          </Text>
        </div>
      </div>

      {renderConteudo()}
    </div>
  );
};

export default PortalProfessorTurmas;
